/**
 * API wrapper — fetch-based, sends cookies, normalizes errors.
 *
 * Backend convention (success): { success: true, data: ... }
 * Backend convention (error):   { success: false, error: { code, message, retry_after_seconds? } }
 *
 * Legacy fallbacks supported during transition:
 *   - { code, message, ... } (flat error)
 *   - plain string `[ERR_XXX] message`
 */

import { reportNetworkState } from './network-status';

export const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
export const CSRF_COOKIE_NAME = (import.meta.env.VITE_CSRF_COOKIE_NAME as string | undefined) ?? 'vanagreen_csrf';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: unknown;
  public readonly retryAfterSeconds?: number;
  /**
   * Same id the backend logs and tags in Sentry — shown to the user as the
   * reference code. Never null for errors raised by `api()`.
   */
  public readonly requestId: string | null;

  constructor(opts: {
    code: string;
    message: string;
    status: number;
    details?: unknown;
    retryAfterSeconds?: number;
    requestId?: string | null;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
    this.details = opts.details;
    this.retryAfterSeconds = opts.retryAfterSeconds;
    this.requestId = opts.requestId ?? null;
  }
}

// Generated on our side and sent with the call, so even a request that never
// reached the server (offline, DNS) has a code the user can quote.
function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Hook for the auth provider to receive 401 / SESSION_EXPIRED notifications
 * from any request. Set once at app mount.
 */
type UnauthorizedHandler = (error: ApiError) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: ApiRequestOptions['query']): string {
  const url = new URL(path.startsWith('http') ? path : `${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function parseErrorBody(raw: string): {
  code: string;
  message: string;
  details?: unknown;
  retryAfterSeconds?: number;
} {
  // Try JSON first
  try {
    const parsed = JSON.parse(raw) as {
      success?: boolean;
      error?: { code?: string; message?: string; retry_after_seconds?: number } | string;
      code?: string;
      message?: string;
      retry_after_seconds?: number;
    };
    if (parsed && typeof parsed === 'object') {
      // New envelope: { success: false, error: { code, message, details?, retry_after_seconds? } }
      if (parsed.error && typeof parsed.error === 'object') {
        const errObj = parsed.error as {
          code?: string;
          message?: string;
          retry_after_seconds?: number;
          details?: unknown;
        };
        return {
          code: errObj.code ?? 'ERR_UNKNOWN',
          message: errObj.message ?? raw,
          retryAfterSeconds: errObj.retry_after_seconds,
          details: errObj.details ?? parsed,
        };
      }
      // Legacy flat: { code, message, retry_after_seconds? }
      return {
        code: parsed.code ?? 'ERR_UNKNOWN',
        message: parsed.message ?? (typeof parsed.error === 'string' ? parsed.error : raw),
        retryAfterSeconds: parsed.retry_after_seconds,
        details: parsed,
      };
    }
  } catch {
    // not JSON
  }
  // Try `[ERR_XXX] message` format
  const match = /^\[(ERR_[A-Z0-9_]+)\]\s*(.*)$/.exec(raw.trim());
  if (match && match[1]) {
    return { code: match[1], message: match[2] ?? '' };
  }
  return { code: 'ERR_UNKNOWN', message: raw || 'Unknown error' };
}



export async function api<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { body, headers, query, ...rest } = options;
  const url = buildUrl(path, query);
  const method = (rest.method ?? 'GET').toUpperCase();

  const csrfHeaders: Record<string, string> = {};
  if (UNSAFE_METHODS.has(method)) {
    const csrf = getCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      csrfHeaders['x-csrf-token'] = csrf;
    }
  }

  const isFormData = body instanceof FormData;
  const requestId = newRequestId();

  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      'x-request-id': requestId,
      ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...csrfHeaders,
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = isFormData ? body : (typeof body === 'string' ? body : JSON.stringify(body));
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    // An aborted request (navigation, React Query cancel) is not an outage.
    if (!(err instanceof DOMException && err.name === 'AbortError')) reportNetworkState(false);
    throw new ApiError({
      code: 'ERR_NETWORK',
      message: err instanceof Error ? err.message : 'Network error',
      status: 0,
      requestId,
    });
  }

  reportNetworkState(true);
  const text = await response.text();

  if (!response.ok) {
    const parsed = parseErrorBody(text);
    const apiError = new ApiError({
      code: parsed.code,
      message: parsed.message,
      status: response.status,
      details: parsed.details,
      retryAfterSeconds: parsed.retryAfterSeconds,
      requestId: response.headers.get('x-request-id') ?? requestId,
    });
    if (response.status === 401 || apiError.code === 'SESSION_EXPIRED') {
      unauthorizedHandler?.(apiError);
    }
    throw apiError;
  }

  if (!text) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export const http = {
  get: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    api<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method'>) =>
    api<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method'>) =>
    api<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method'>) =>
    api<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    api<T>(path, { ...options, method: 'DELETE' }),
};

// Convenience aliases for ergonomic call sites.
export const apiGet = http.get;
export const apiPost = http.post;
export const apiPut = http.put;
export const apiPatch = http.patch;
export const apiDelete = http.delete;
